import type { RouteLocationNormalized, NavigationFailure } from "vue-router";
import { useUserStore } from "@/config/store/userStore";
import { API_MAIN_VERSION, BASE_URL } from "@/config/constantes";

const SKIP_MODULES = new Set(["AUTH", "MANAGEMENT", "SUPER_ADMIN"]);
const MODULE_RE = /^[A-Z][A-Z0-9_]{0,39}$/;
const DEDUP_MS = 60_000;

const lastSeenAt = new Map<string, number>();

const pruneLastSeen = (now: number) => {
  if (lastSeenAt.size < 200) return;
  for (const [key, at] of lastSeenAt) {
    if (now - at > DEDUP_MS * 2) lastSeenAt.delete(key);
  }
};

/** `SUPER_ADMIN_USERS` is as internal as `SUPER_ADMIN`: skip the sub-modules too. */
const isSkipped = (module: string) =>
  [...SKIP_MODULES].some((skip) => module === skip || module.startsWith(`${skip}_`));

const normalizeModule = (value: unknown) => {
  const module = String(value || "")
    .trim()
    .toUpperCase();
  if (!MODULE_RE.test(module) || isSkipped(module)) return null;
  return module;
};

/**
 * A route that declares a module is trusted on that alone. Falling back to the
 * route name when `normalizeModule` returned null defeated the skip list:
 * `SUPER_ADMIN` was dropped, then `SuperAdminProfiles` came back through the
 * fallback as `SUPER_ADMIN_PROFILES` and got counted as client usage.
 */
const moduleFromRoute = (to: RouteLocationNormalized) => {
  const declared = String(to.meta.moduleName || "").trim();
  if (declared) return normalizeModule(declared);
  return normalizeModule(
    typeof to.name === "string" ? to.name.replace(/([a-z])([A-Z])/g, "$1_$2") : "",
  );
};

/**
 * The deepest matched record carries the full pattern, parents included
 * (`/evaluation/:evaluationId`): every id of a page folds into it server side,
 * while `to.path` keeps the concrete one for the drill-down.
 */
const pageFromRoute = (to: RouteLocationNormalized) => to.matched.at(-1)?.path ?? null;

const trimTrailingSlash = (value: string) => (value.length > 1 ? value.replace(/\/+$/, "") : value);

/**
 * Fire-and-forget page visit. Same-origin keepalive fetch so the JWT header
 * is sent — `sendBeacon` cannot. Deduped here per concrete path; the server
 * dedupes again, per user × module and per user × path (60s each).
 */
export const trackPageView = (to: RouteLocationNormalized, failure?: NavigationFailure | void) => {
  if (failure || !to.meta.requiresAuth) return;

  const userStore = useUserStore();
  const user = userStore.user;
  const token = userStore.token;
  if (!token || !user?._firmId) return;
  if (user.role === "api" || user.role === "candidate") return;

  const module = moduleFromRoute(to);
  if (!module) return;
  const path = trimTrailingSlash(to.path);

  const now = Date.now();
  const previous = lastSeenAt.get(path);
  if (previous && now - previous < DEDUP_MS) return;
  lastSeenAt.set(path, now);
  pruneLastSeen(now);

  fetch(`${BASE_URL}/${API_MAIN_VERSION}/userSessions/page-view`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ module, page: pageFromRoute(to), path }),
  }).catch(() => {});
};
